// for the table rows in the operators flight application page
// monitors the flight channel
// also allows to activate and deactivate

import { Controller } from "@hotwired/stimulus"
import consumer from '../channels/consumer';

export default class extends Controller {
  static targets = ['button'];

  connect() {
    // The buttons render hidden and only this fetch reveals them. It runs first and
    // never depends on the cable: a subscribe that fails to confirm (the production
    // fault) or throws during setup must not leave the operator without buttons.
    this.syncActivationStatus();

    try {
      this.channel = consumer.subscriptions.create('FlightChannel', {
        connected: this._cableConnected.bind(this),
        disconnected: this._cableDisconnected.bind(this),
        received: this._cableReceived.bind(this),
      });
    } catch (error) {
      console.error('APcable FlightChannel subscription failed; buttons still synced over HTTP', error);
      window.airbrake?.notify({ error: error, context: { component: 'APcable FlightChannel subscribe' } });
    }
  }

  disconnect() {
    if (this.channel) this.channel.unsubscribe();
  }

  // Broadcasts sent while this browser was disconnected are lost, so re-sync
  // from the database on every (re)connect rather than trusting the rows.
  syncActivationStatus() {
    let _this = this;
    let ids = this.buttonTargets.map(button => button.dataset.flight);
    if (ids.length === 0) return;

    fetch('/operator/flight_applications/activation_status?ids=' + ids, {
      headers: { 'Accept': 'application/json' }
    })
    .then(function(response) {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then(function(data) {
      for (let id in data) {
        let element = document.getElementById(`flight-${id}`);
        if (!element) continue;

        let status = data[id];
        if (status['approved_or_acknowledged'] !== 'true') continue;

        _this.updateElement(element, {
          activated: status['activated'] === 'true',
          canActivate: status['can_activate'] === 'true'
        });
      }
    })
    .catch(function(error) {
      if (error.name === 'AbortError') return;
      console.error('APcable failed to fetch operator activation status:', error);
      window.airbrake?.notify({ error: error, context: { component: 'APcable operator activation sync' } });
    });
  }

  updateElement(element, { activated, canActivate }) {
    let activateBtn = element.querySelector('.active-link');
    let deactivateBtn = element.querySelector('.inactive-link');
    let placeholder = element.querySelector('.activation-placeholder');
    let completeLink = element.querySelector('.complete-link');

    // The buttons are only rendered while the assessment offers activation
    // (see Assessment#show_flight_activation_controls?), so they may be absent

    // Update highlight and deactivate button based on database activation status
    if (activated) {
      element.classList.add('flight-activated');
      element.dataset.activated = 'true';
      if (deactivateBtn) deactivateBtn.classList.remove('d-none');
    } else {
      element.classList.remove('flight-activated');
      element.dataset.activated = 'false';
      if (deactivateBtn) deactivateBtn.classList.add('d-none');
    }

    // Update the Complete button confirm message to match current activation state
    if (completeLink) {
      if (activated) {
        completeLink.dataset.turboConfirm = 'Your flight is still activated. Continuing will deactivate the flight and mark the application as completed. This cannot be undone. Only continue if you have completed all activities.';
      } else {
        completeLink.dataset.turboConfirm = 'Mark this application as completed? This cannot be undone. Only continue if you have completed all activities.';
      }
    }

    // Show/hide activate button (only during flight time and not already activated)
    if (activateBtn) {
      if (canActivate && !activated) {
        activateBtn.classList.remove('d-none');
      } else {
        activateBtn.classList.add('d-none');
      }
    }

    // Hide placeholder if any button is shown
    if (placeholder) {
      if (canActivate || activated) {
        placeholder.classList.add('d-none');
      } else {
        placeholder.classList.remove('d-none');
      }
    }
  }

  activate(event) {
    event.preventDefault();
    const url = event.target.dataset.url;
    const csrfToken = document.head.querySelector('meta[name="csrf-token"]')?.content;
    return fetch(url, {
      method: 'GET',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/javascript'
      }
    });
  }

  deactivate(event) {
    event.preventDefault();
    const url = event.target.dataset.url;
    const csrfToken = document.head.querySelector('meta[name="csrf-token"]')?.content;
    return fetch(url, {
      method: 'GET',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'text/javascript'
      }
    });
  }

  _cableReceived(data) {
    // Called when there's incoming data on the websocket for this channel
    const flightId = data.flight;
    const element = document.getElementById(`flight-${flightId}`);
    if (element) {
      this.updateElement(element, {
        activated: data.activated,
        canActivate: data.can_activate
      });
    }
  }

  _cableConnected() {
    this.syncActivationStatus();
  }

  _cableDisconnected() {}

}
